import React, { useState, useEffect, useRef } from 'react';
import { Save, Plus, Trash2, Upload, X, GripVertical, Eye, Heart, Shield, Headphones } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL (handles both relative and absolute paths)
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API}${path}`;
};

const AdminAboutUs = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_headline: "We're on a mission to ensure every family is protected.",
    hero_subtext: "Making estate planning simple, accessible, and affordable for everyone.",
    hero_image: null,
    values: [
      { id: '1', icon: 'simplicity', title: 'Simplicity', description: 'Create your will in minutes, not hours' },
      { id: '2', icon: 'security', title: 'Security', description: 'Bank-grade encryption for your data' },
      { id: '3', icon: 'support', title: 'Support', description: 'Expert help whenever you need it' }
    ],
    testimonial: {
      quote: "WillKart gave us peace of mind knowing our children's future is secure.",
      author: "Satisfied Customer",
      role: "Parent of 2"
    },
    testimonials: [
      { quote: "WillKart gave us peace of mind knowing our children's future is secure.", author: "Satisfied Customer", role: "Parent of 2" }
    ],
    team: [],
    team_heading: "Meet Our Team",
    team_subtext: "Passionate experts dedicated to protecting your family's future",
    cta_text: "Start Your Will Today",
    show_testimonial: true,
    show_team: true,
    show_values: true
  });

  const fileInputRef = useRef(null);
  const teamPhotoRefs = useRef({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/api/admin/about-us`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch about us content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/api/admin/about-us`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('About Us content saved successfully');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'about_hero');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/api/admin/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setContent(prev => ({ ...prev, hero_image: response.data.url }));
      toast.success('Hero image uploaded');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleTeamPhotoUpload = async (e, memberId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be less than 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'team_photo');
    formData.append('member_id', memberId);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/api/admin/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setContent(prev => ({
        ...prev,
        team: prev.team.map(m => 
          m.id === memberId ? { ...m, photo: response.data.url } : m
        )
      }));
      toast.success('Photo uploaded');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload photo');
    }
  };

  const addTeamMember = () => {
    const newMember = {
      id: Date.now().toString(),
      name: '',
      role: '',
      photo: null,
      linkedin: '',
      twitter: '',
      email: ''
    };
    setContent(prev => ({
      ...prev,
      team: [...prev.team, newMember]
    }));
  };

  const removeTeamMember = (id) => {
    setContent(prev => ({
      ...prev,
      team: prev.team.filter(m => m.id !== id)
    }));
  };

  const updateTeamMember = (id, field, value) => {
    setContent(prev => ({
      ...prev,
      team: prev.team.map(m => 
        m.id === id ? { ...m, [field]: value } : m
      )
    }));
  };

  const addValue = () => {
    const newValue = {
      id: Date.now().toString(),
      icon: 'simplicity',
      title: '',
      description: ''
    };
    setContent(prev => ({
      ...prev,
      values: [...prev.values, newValue]
    }));
  };

  const removeValue = (id) => {
    setContent(prev => ({
      ...prev,
      values: prev.values.filter(v => v.id !== id)
    }));
  };

  const updateValue = (id, field, value) => {
    setContent(prev => ({
      ...prev,
      values: prev.values.map(v => 
        v.id === id ? { ...v, [field]: value } : v
      )
    }));
  };

  const iconOptions = [
    { value: 'simplicity', label: 'Heart (Simplicity)', icon: <Heart size={16} /> },
    { value: 'security', label: 'Shield (Security)', icon: <Shield size={16} /> },
    { value: 'support', label: 'Headphones (Support)', icon: <Headphones size={16} /> }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">About Us Page</h1>
          <p className="text-slate-400 mt-1">Customize your About Us page content</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => window.open('/about', '_blank')}
            className="border-slate-600"
          >
            <Eye size={16} className="mr-2" />
            Preview
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900"
          >
            <Save size={16} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h2 className="text-lg font-semibold text-white mb-4">Hero Section</h2>
        
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Headline</Label>
            <Input
              value={content.hero_headline}
              onChange={(e) => setContent(prev => ({ ...prev, hero_headline: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white mt-1"
              placeholder="Main headline..."
            />
          </div>
          
          <div>
            <Label className="text-slate-300">Subtext</Label>
            <Input
              value={content.hero_subtext}
              onChange={(e) => setContent(prev => ({ ...prev, hero_subtext: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white mt-1"
              placeholder="Supporting text..."
            />
          </div>

          <div>
            <Label className="text-slate-300">Hero Image (Optional)</Label>
            <div className="mt-2 flex items-center gap-4">
              {content.hero_image ? (
                <div className="relative">
                  <img 
                    src={getImageUrl(content.hero_image)} 
                    alt="Hero" 
                    className="h-32 w-auto rounded-lg object-cover"
                  />
                  <button
                    onClick={() => setContent(prev => ({ ...prev, hero_image: null }))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="h-32 w-48 border-2 border-dashed border-slate-600 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-amber-500 hover:text-amber-500 transition-colors"
                >
                  <Upload size={24} className="mb-2" />
                  <span className="text-sm">Upload Image</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleHeroImageUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Values Section</h2>
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input
                type="checkbox"
                checked={content.show_values}
                onChange={(e) => setContent(prev => ({ ...prev, show_values: e.target.checked }))}
                className="rounded border-slate-600 bg-slate-800 text-amber-500"
              />
              <span className="text-slate-400">Show values section</span>
            </label>
          </div>
          <Button onClick={addValue} variant="outline" size="sm" className="border-slate-600">
            <Plus size={16} className="mr-1" /> Add Value
          </Button>
        </div>

        <div className="space-y-4">
          {content.values.map((value, index) => (
            <div key={value.id} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="text-slate-500 cursor-move">
                  <GripVertical size={20} />
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-slate-400 text-xs">Icon</Label>
                    <select
                      value={value.icon}
                      onChange={(e) => updateValue(value.id, 'icon', e.target.value)}
                      className="w-full mt-1 bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-white text-sm"
                    >
                      {iconOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Title</Label>
                    <Input
                      value={value.title}
                      onChange={(e) => updateValue(value.id, 'title', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="e.g., Simplicity"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-slate-400 text-xs">Description</Label>
                    <Input
                      value={value.description}
                      onChange={(e) => updateValue(value.id, 'description', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="Short description..."
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeValue(value.id)}
                  className="text-slate-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Testimonials</h2>
            <p className="text-slate-500 text-xs mt-1">{content.testimonials?.length || 0}/3 testimonials</p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={content.show_testimonial}
                onChange={(e) => setContent(prev => ({ ...prev, show_testimonial: e.target.checked }))}
                className="rounded border-slate-600 bg-slate-800 text-amber-500"
              />
              <span className="text-slate-400">Show testimonials</span>
            </label>
            {(content.testimonials?.length || 0) < 3 && (
              <Button
                type="button"
                size="sm"
                onClick={() => setContent(prev => ({
                  ...prev,
                  testimonials: [...(prev.testimonials || []), { quote: '', author: '', role: '' }]
                }))}
                className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            )}
          </div>
        </div>

        {(!content.testimonials || content.testimonials.length === 0) && (
          <p className="text-slate-500 text-sm text-center py-6">No testimonials yet. Click "Add" to create one.</p>
        )}

        <div className="space-y-4">
          {(content.testimonials || []).map((t, idx) => (
            <div key={idx} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700 relative">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-slate-500 font-medium">Testimonial {idx + 1}</span>
                <button
                  type="button"
                  onClick={() => setContent(prev => ({
                    ...prev,
                    testimonials: prev.testimonials.filter((_, i) => i !== idx)
                  }))}
                  className="text-red-400 hover:text-red-300 transition-colors p-1"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300 text-xs">Quote</Label>
                  <textarea
                    value={t.quote || ''}
                    onChange={(e) => {
                      const updated = [...content.testimonials];
                      updated[idx] = { ...updated[idx], quote: e.target.value };
                      setContent(prev => ({ ...prev, testimonials: updated }));
                    }}
                    className="w-full mt-1 bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-white text-sm min-h-[80px]"
                    placeholder="Customer testimonial..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs">Author Name</Label>
                    <Input
                      value={t.author || ''}
                      onChange={(e) => {
                        const updated = [...content.testimonials];
                        updated[idx] = { ...updated[idx], author: e.target.value };
                        setContent(prev => ({ ...prev, testimonials: updated }));
                      }}
                      className="bg-slate-800 border-slate-600 text-white mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Author Role/Title</Label>
                    <Input
                      value={t.role || ''}
                      onChange={(e) => {
                        const updated = [...content.testimonials];
                        updated[idx] = { ...updated[idx], role: e.target.value };
                        setContent(prev => ({ ...prev, testimonials: updated }));
                      }}
                      className="bg-slate-800 border-slate-600 text-white mt-1 text-sm"
                      placeholder="e.g., Parent of 2"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Section */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Team Section</h2>
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input
                type="checkbox"
                checked={content.show_team}
                onChange={(e) => setContent(prev => ({ ...prev, show_team: e.target.checked }))}
                className="rounded border-slate-600 bg-slate-800 text-amber-500"
              />
              <span className="text-slate-400">Show team section</span>
            </label>
          </div>
          <Button onClick={addTeamMember} variant="outline" size="sm" className="border-slate-600">
            <Plus size={16} className="mr-1" /> Add Team Member
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <Label className="text-slate-300">Section Heading</Label>
            <Input
              value={content.team_heading}
              onChange={(e) => setContent(prev => ({ ...prev, team_heading: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-slate-300">Section Subtext</Label>
            <Input
              value={content.team_subtext}
              onChange={(e) => setContent(prev => ({ ...prev, team_subtext: e.target.value }))}
              className="bg-slate-900 border-slate-600 text-white mt-1"
            />
          </div>
        </div>

        <div className="space-y-4">
          {content.team.map((member) => (
            <div key={member.id} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="flex gap-4">
                {/* Photo Upload */}
                <div className="flex-shrink-0">
                  {member.photo ? (
                    <div className="relative">
                      <img 
                        src={getImageUrl(member.photo)} 
                        alt={member.name} 
                        className="w-24 h-24 rounded-xl object-cover"
                      />
                      <button
                        onClick={() => updateTeamMember(member.id, 'photo', null)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => teamPhotoRefs.current[member.id]?.click()}
                      className="w-24 h-24 border-2 border-dashed border-slate-600 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-amber-500 hover:text-amber-500 transition-colors"
                    >
                      <Upload size={20} className="mb-1" />
                      <span className="text-xs">Photo</span>
                    </button>
                  )}
                  <input
                    ref={el => teamPhotoRefs.current[member.id] = el}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleTeamPhotoUpload(e, member.id)}
                    className="hidden"
                  />
                </div>

                {/* Member Details */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Name *</Label>
                    <Input
                      value={member.name}
                      onChange={(e) => updateTeamMember(member.id, 'name', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Role *</Label>
                    <Input
                      value={member.role}
                      onChange={(e) => updateTeamMember(member.id, 'role', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="e.g., Founder & CEO"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">LinkedIn URL</Label>
                    <Input
                      value={member.linkedin || ''}
                      onChange={(e) => updateTeamMember(member.id, 'linkedin', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Twitter URL</Label>
                    <Input
                      value={member.twitter || ''}
                      onChange={(e) => updateTeamMember(member.id, 'twitter', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Email</Label>
                    <Input
                      value={member.email || ''}
                      onChange={(e) => updateTeamMember(member.id, 'email', e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white mt-1"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <button
                  onClick={() => removeTeamMember(member.id)}
                  className="text-slate-500 hover:text-red-400 transition-colors self-start"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}

          {content.team.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No team members added yet. Click "Add Team Member" to get started.
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h2 className="text-lg font-semibold text-white mb-4">Call to Action</h2>
        
        <div>
          <Label className="text-slate-300">CTA Button Text</Label>
          <Input
            value={content.cta_text}
            onChange={(e) => setContent(prev => ({ ...prev, cta_text: e.target.value }))}
            className="bg-slate-900 border-slate-600 text-white mt-1 max-w-md"
            placeholder="Start Your Will Today"
          />
        </div>
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 px-8"
        >
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AdminAboutUs;
